import DollarCoinsIcon from '@streamlinehq/streamlinehq/img/streamline-light/money-payments-finance/accounting-billing/accounting-coins.svg';
import DollarsIcon from '@streamlinehq/streamlinehq/img/streamline-light/money-payments-finance/currencies/currency-dollar-circle.svg';
import { FinalImpactScoreType } from '@upright/common/model/output/scores/types';
import { Valence } from '@upright/common/model/types';
import { friendlyLowerCaseScoreUnit } from '@upright/common/utils';
import { E2eTestElementId } from '@upright/test-ids/dom-elements';
import { formatNumberEfficiently, NumberType } from '@upright/ui/common/generic/utilities/format-numbers-efficiently';
import { useNetImpactLensContext } from '@upright/ui/common/upright/core/impact-lenses/net-impact/common/use-net-impact-lens-context';
import { FigureBoxesWithExplanation } from '@upright/ui/common/upright/core/impact-lenses/net-impact/info-boxes/single-impact/common/figures-boxes-with-explanation';
import { useImpactDetailsRevenue } from '@upright/ui/common/upright/core/impact-lenses/net-impact/info-boxes/single-impact/common/use-impact-details-revenue';
import { getImpactSizeFactors } from '@upright/ui/untidy/redux/selectors';
import { get } from 'lodash';
import { useSelector } from 'react-redux';
import { PlainIconFigureBox } from '../common/figure-box';

export function SingleImpactInfoBoxMonetaryFigureBoxes() {
  const { isFallback } = useImpactDetailsRevenue();
  const boxes = isFallback
    ? [<RelativeMonetaryFigureBox key="rel" />]
    : [<AbsoluteMonetaryFigureBox key="abs" />, <RelativeMonetaryFigureBox key="rel" />];
  return <FigureBoxesWithExplanation boxes={boxes} />;
}

export function AbsoluteMonetaryFigureBox() {
  const context = useNetImpactLensContext();
  const { centsPerDollar, dollarsPerYear, intensity } = useMoneyFiguresForSelection();

  if (!context || !dollarsPerYear || !centsPerDollar || !intensity) {
    return <></>;
  }
  const { valence } = context.state;
  if (!valence) {
    return <></>;
  }
  const formattedValue = formatNumberEfficiently(dollarsPerYear, NumberType.DOLLARS, 3);
  const relativeMode = context.options.relativeMode;

  return (
    <PlainIconFigureBox
      numberText={formattedValue}
      textBelowNumber="dollars per year"
      icon={DollarsIcon}
      highlighted={!relativeMode}
      explanationText={
        <>
          In monetary terms, this corresponds to a total {valence === Valence.POSITIVE ? 'benefit' : 'cost'} of{' '}
          <b>{formattedValue}</b> per year...
        </>
      }
      testId={E2eTestElementId.ABSOLUTE_MONETARY_FIGURE_BOX}
    />
  );
}

function RelativeMonetaryFigureBox() {
  const context = useNetImpactLensContext();
  const { centsPerDollar, dollarsPerYear, intensity, isFallback } = useMoneyFiguresForSelection();
  if (!context || !dollarsPerYear || !centsPerDollar || !intensity) {
    return <></>;
  }
  const relativeMode = context.options.relativeMode;
  const formattedValue = centsPerDollar > 1 ? centsPerDollar.toPrecision(3) : centsPerDollar.toPrecision(1);
  const intensityComparisonText =
    intensity > 1 ? (
      <>
        which is <b>{((intensity - 1) * 100).toFixed(0)}%</b> more than the average company
      </>
    ) : (
      <>
        which is <b>{((1 - intensity) * 100).toFixed(0)}%</b> less than the average company
      </>
    );

  const textStart = !isFallback ? (
    '...or'
  ) : (
    <>
      In monetary terms<sup>2</sup>, this amounts to
    </>
  );

  return (
    <PlainIconFigureBox
      numberText={formattedValue}
      textBelowNumber={friendlyLowerCaseScoreUnit(FinalImpactScoreType.CENTS_PER_DOLLAR)}
      textAboveNumber={null}
      highlighted={relativeMode}
      icon={DollarCoinsIcon}
      explanationText={
        <>
          {textStart}{' '}
          <b>
            {formattedValue} {friendlyLowerCaseScoreUnit(FinalImpactScoreType.CENTS_PER_DOLLAR)}
          </b>{' '}
          generated by the company, {intensityComparisonText}
        </>
      }
      testId={E2eTestElementId.RELATIVE_MONETARY_FIGURE_BOX}
    />
  );
}

export function useMoneyFiguresForSelection() {
  const context = useNetImpactLensContext();
  const impactSizeFactors = useSelector(getImpactSizeFactors);
  const { isFallback, revenue } = useImpactDetailsRevenue();

  const empty = {
    centsPerDollar: undefined,
    dollarsPerYear: undefined,
    intensity: undefined,
    isFallback,
  };

  if (!context) {
    return empty;
  }

  const selection = context.state;
  const impact = selection.impact;
  const valence = selection.valence;

  const scoreReader = context.options.scoreReader;
  const centsPerDollarScoreReader = scoreReader.clone({ scoreType: FinalImpactScoreType.CENTS_PER_DOLLAR, revenue });
  const dollarsPerYearScoreReader = scoreReader.clone({ scoreType: FinalImpactScoreType.DOLLARS_PER_YEAR, revenue });
  const relativeScoreReader = scoreReader.clone({ scoreType: FinalImpactScoreType.RELATIVE, revenue });
  const relativeScore = relativeScoreReader.readScore(impact, undefined, undefined, valence);

  if (!impact || !valence) {
    return empty;
  }

  const economicCost = get(impactSizeFactors, [impact, valence], undefined)?.economicCost;

  return {
    centsPerDollar: centsPerDollarScoreReader.readScore(impact, undefined, undefined, valence),
    dollarsPerYear: dollarsPerYearScoreReader.readScore(impact, undefined, undefined, valence),
    intensity: economicCost && relativeScore ? relativeScore / economicCost : undefined,
    isFallback,
  };
}
